// URL base da API
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Função auxiliar para obter o token de autenticação do localStorage
const getAuthToken = (): string | null => {
  try {
    return localStorage.getItem('authToken');
  } catch (error) {
    console.error('Erro ao obter token de autenticação:', error);
    return null;
  }
};

// Função para criar headers com token de autenticação
const getAuthHeaders = (contentType?: string): HeadersInit => {
  const token = getAuthToken();
  const headers: HeadersInit = {};
  
  if (token) {
    // Garantir que o token seja passado no header Authentication
    headers['Authentication'] = `Bearer ${token}`;
    // Manter também no Authorization para compatibilidade
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    console.warn('Token de autenticação não encontrado no localStorage');
  }
  
  if (contentType) {
    headers['Content-Type'] = contentType;
  }
  
  return headers;
};

const getBackendErrorMessage = async (response: Response, fallbackMessage: string): Promise<string> => {
  try {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const data = await response.json();
      if (data && typeof data === 'object') {
        const anyData = data as any;
        const message = typeof anyData.message === 'string' ? anyData.message
          : typeof anyData.Mensagem === 'string' ? anyData.Mensagem
          : undefined;
        if (message && message.trim()) return message;
      }
    } else {
      const text = await response.text();
      if (text && text.trim()) return text;
    }
  } catch {
    // Ignorar falha ao ler body; usar fallback
  }

  return fallbackMessage;
};

// Interface para os planos de upgrade disponíveis
export interface UpgradeablePlan {
  id: string;
  plan: string;
  enabled: boolean;
}

// Interface para cada emissão individual
export interface Emission {
  id: string;
  planId: string;
  planCode: string;
  holderType: string;
  holderName: string;
  holderDocument: string;
  status: string;
  pmsMessage?: string;
  policyNo: string;
  contractNo: string;
  commissionType: number;
  initialEffectiveDate: string;
  finalEffectiveDate: string;
  emittedAt: string;
}

// Interface para os dados de transação retornados pela API
export interface Transaction {
  id: number | string;
  ticketNo: string;
  customerName: string;
  idNo: string;
  chassisNo: string;
  plateNo: string;
  initialEffectiveDate: string;
  finalEffectiveDate: string | null;
  status: string;
  situacao?: string | null;
  policyNo: string | null;
  contractNo?: string;
  planId?: string;
  planCode?: string;
  planInsuranceHolder?: string;
  createdAt: string;
  updatedAt: string;
  upgradeablePlans?: UpgradeablePlan[];
  emissions?: Emission[];
}


// Interface para a resposta do upload
export interface UploadResponse {
  success: boolean;
  message: string;
}

// Interface para o endereço do segurado
export interface SeguradoEndereco {
  Endereco: string;
  Numero: string;
  Complemento: string;
  Bairro: string;
  Cidade: string;
  UF: string;
  CEP: string;
}

// Interface para os dados do segurado
export interface Segurado {
  CpfCnpj: string;
  NumeroDocumento: string;
  OrgaoEmissorDocumento: string;
  Nome: string;
  DataNascimento: string;
  Genero: number;
  EstadoCivil: number;
  Endereco: SeguradoEndereco;
  Email: string;
  TelefoneSMS: string;
  PoliticamenteExposta: boolean;
}

// Interface para os dados do cliente da apólice (mantida para compatibilidade)
export interface PolicyCustomer {
  CustomerType: string;
  CustomerName: string;
  DateOfBirth: string;
  IdType: string;
  IdNo: string;
  IsPolicyHolder: string;
  IsInsured: string;
  IsCoInsured: string;
  IsBeneficiary: string;
  IsPep: string;
  ConsentData: string;
  ConsentEmail: string;
  Mobile: string;
  Email: string;
  PostCode: string;
  Address: string;
  AddressNumber: string;
  AddressComplement: string;
  Suburb: string;
  City: string;
  State: string;
  Gender: string;
  Nationality: string;
}

// Interface para a resposta da atualização da data de efetivação
export interface UpdateEffectiveDateResponse {
  id: number;
  ticketNo: string;
  chassisNo: string;
  plateNo: string;
  initialEffectiveDate: string;
  finalEffectiveDate: string;
  status: string;
  policyNo: string | null;
  updatedAt: string;
}

// Interface para a resposta da emissão no PMS
export interface EmitResponse {
  id: number | string;
  ticketNo: string;
  status: string;
  policyNo: string;
  customerName?: string;
  idNo?: string;
  contractNo?: string;
  pmsRequestJson: string;
  pmsResponseJson: string;
  updatedAt: string;
  emissions?: Emission[];
}

// Serviço para busca de veículos
export const vehicleService = {
  // Função para buscar veículos por placa ou chassi
  searchVehicle: async (value: string): Promise<Transaction[]> => {
    try {
      console.log(`Buscando por: ${value}`);
      
      // Adicionar headers de autenticação
      const headers = getAuthHeaders();
      console.log('Headers de autenticação:', headers);
      
      const response = await fetch(`${API_BASE_URL}/api/v1/contracts/buscar?valor=${encodeURIComponent(value)}`, {
        method: 'GET',
        headers: headers
      });
      if (!response.ok) {
        throw new Error(await getBackendErrorMessage(response, 'Não foi possível buscar os dados do veículo. Verifique a placa/chassi e tente novamente.'));
      }
      const data = await response.json();
      
      // Verificar se a resposta é um objeto único ou um array
      if (data && !Array.isArray(data)) {
        // Se for um objeto único, retorna como um array com um único item
        return [data];
      }
      
      return data;
    } catch (error) {
      console.error('Erro na busca:', error);
      throw error;
    }
  },

  // Função para fazer upload do arquivo PDF da nota fiscal
  uploadInvoicePDF: async (transactionId: number | string, file: File, effectiveDate: string): Promise<UploadResponse> => {
    try {
      // Criar um objeto FormData para enviar o arquivo
      const formData = new FormData();
      formData.append('file', file);
      formData.append('effectiveDate', effectiveDate);
      
      console.log(`Enviando arquivo para transação ID: ${transactionId}`);
      console.log(`Nome do arquivo: ${file.name}`);
      console.log(`Tamanho do arquivo: ${(file.size / 1024).toFixed(2)} KB`);
      console.log(`Data de efetivação: ${effectiveDate}`);
      
      // Enviar o arquivo para a API com autenticação
      const headers = getAuthHeaders();
      // Não adicionar Content-Type para FormData, o navegador adiciona automaticamente com o boundary correto
      // Convertendo para Record para manipular as propriedades
      const headerRecord = headers as Record<string, string>;
      if ('Content-Type' in headerRecord) {
        delete headerRecord['Content-Type'];
      }
      
      const response = await fetch(`${API_BASE_URL}/api/v1/contracts/${transactionId}/nf-pdf`, {
        method: 'POST',
        headers: headers,
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(await getBackendErrorMessage(response, 'Não foi possível enviar o arquivo. Verifique o formato e tente novamente.'));
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erro no upload:', error);
      throw error;
    }
  },
  
  // Função para atualizar a data de efetivação final
  updateFinalEffectiveDate: async (plateNo: string, dataInicioVigenciaEfetiva: string, segurado?: Segurado): Promise<UpdateEffectiveDateResponse> => {
    try {
      console.log(`Atualizando data de efetivação para placa: ${plateNo}`);
      console.log(`Nova data de início de vigência efetiva: ${dataInicioVigenciaEfetiva}`);
      console.log('Dados do segurado:', segurado);
      
      // Preparar o corpo da requisição para a API
      const requestBody: any = {
        DataInicioVigenciaEfetiva: dataInicioVigenciaEfetiva
      };
      
      // Adicionar dados do segurado apenas se fornecido (para CUSTOMER)
      if (segurado) {
        requestBody.Segurado = segurado;
      }
      
      // Código para atualizar a data de efetivação
      const headers = getAuthHeaders('application/json');
      
      const response = await fetch(`${API_BASE_URL}/api/v1/contracts/final-effective-date?valor=${plateNo}`, {
        method: 'PATCH',
        headers: headers,
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        throw new Error(await getBackendErrorMessage(response, 'Não foi possível atualizar a data de efetivação. Por favor, tente novamente.'));
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erro ao atualizar data de efetivação:', error);
      throw error;
    }
  },
  
  // Função para emitir no PMS
  emitInPMS: async (transactionId: number | string): Promise<EmitResponse> => {
    try {
      console.log(`Emitindo no PMS para transação ID: ${transactionId}`);
      
      // Código para emitir no PMS
      const headers = getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/api/v1/contracts/${transactionId}/emitir`, {
        method: 'POST',
        headers: headers
      });
      
      if (!response.ok) {
        throw new Error(await getBackendErrorMessage(response, 'Não foi possível emitir a apólice. Verifique os dados e tente novamente.'));
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erro ao emitir no PMS:', error);
      throw error;
    }
  },
  
  // Função para baixar apólice
  downloadPolicy: async (transactionId: number | string, emissionId?: string): Promise<void> => {
    try {
      // Construir URL com emissionId como query parameter se fornecido
      const url = emissionId 
        ? `${API_BASE_URL}/api/v1/product/download/${transactionId}?emissionId=${emissionId}`
        : `${API_BASE_URL}/api/v1/product/download/${transactionId}`;
      
      console.log(`Baixando apólice via: ${url}`);
      
      const headers = getAuthHeaders();
      
      const response = await fetch(url, {
        method: 'POST',
        headers: headers
      });
      
      if (!response.ok) {
        throw new Error(await getBackendErrorMessage(response, 'Não foi possível baixar a apólice. Por favor, tente novamente.'));
      }
      
      // Obter o nome do arquivo do header Content-Disposition ou usar um nome padrão
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `apolice_${transactionId}${emissionId ? `_${emissionId}` : ''}.pdf`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // Converter a resposta para blob
      const blob = await response.blob();
      
      // Criar um link temporário para download
      const url_obj = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url_obj;
      link.download = filename;
      
      // Adicionar o link ao DOM, clicar e remover
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpar o URL do objeto
      window.URL.revokeObjectURL(url_obj);
      
    } catch (error) {
      console.error('Erro ao baixar apólice:', error);
      throw error;
    }
  },
  
  // Função para fazer upgrade de plano
  upgradePlan: async (contractId: string, targetPlanId: string): Promise<Transaction> => {
    try {
      console.log(`Fazendo upgrade do contrato ${contractId} para o plano ${targetPlanId}`);
      
      const headers = getAuthHeaders('application/json');
      
      const response = await fetch(`${API_BASE_URL}/api/v1/contracts/${contractId}/upgrade`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          targetPlanId: targetPlanId
        })
      });
      
      if (!response.ok) {
        throw new Error(await getBackendErrorMessage(response, 'Não foi possível atualizar o plano. Por favor, tente novamente.'));
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erro ao fazer upgrade:', error);
      throw error;
    }
  }
  ,

  // Função para fazer downgrade do plano (reverter upgrade)
  downgradePlan: async (contractId: string, targetPlanId?: string): Promise<Transaction> => {
    try {
      console.log(`Fazendo downgrade do contrato ${contractId}${targetPlanId ? ` para o plano ${targetPlanId}` : ''}`);

      const headers = getAuthHeaders('application/json');
      const hasBody = !!targetPlanId;

      const response = await fetch(`${API_BASE_URL}/api/v1/contracts/${contractId}/downgrade`, {
        method: 'POST',
        headers: headers,
        ...(hasBody ? { body: JSON.stringify({ targetPlanId }) } : {})
      });

      if (!response.ok) {
        throw new Error(await getBackendErrorMessage(response, 'Não foi possível reverter o plano. Por favor, tente novamente.'));
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erro ao fazer downgrade:', error);
      throw error;
    }
  }
};
